import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import {
  Flex,
  Text,
  Button,
  Box,
  Badge,
  Card,
  TextField,
  IconButton,
  Table,
  ScrollArea,
  Theme,
  Tooltip,
} from '@radix-ui/themes';
import {
  ListBulletIcon,
  BellIcon,
  GearIcon,
  PlusIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
  Pencil1Icon,
  TrashIcon,
  PlayIcon,
  PauseIcon,
  LockClosedIcon,
} from '@radix-ui/react-icons';
import { ChipType } from './index';
import { useTheme } from '../../contexts/ThemeContext';
import './InstrumentationPanel.css';

export interface InstrumentationItem {
  id: string;
  name: string;
  description?: string;
  createdAt?: string;
  status?: 'active' | 'paused' | 'draft';
  readonly?: boolean;
}

interface InstrumentationPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: ChipType;
  entityAddress: string;
  entityName?: string;
  items?: InstrumentationItem[];
  onCreateNew?: () => void;
  onItemClick?: (item: InstrumentationItem) => void;
  onItemEdit?: (item: InstrumentationItem) => void;
  onItemDelete?: (item: InstrumentationItem) => void;
  onItemToggle?: (item: InstrumentationItem) => void;
}

const typeConfig: Record<
  ChipType,
  {
    icon: React.ReactNode;
    title: string;
    singular: string;
    description: string;
    emptyTitle: string;
    emptyMessage: string;
    color: 'blue' | 'amber' | 'violet';
    basePath: string;
    createPath: string;
  }
> = {
  list: {
    icon: <ListBulletIcon width={20} height={20} />,
    title: 'Lists',
    singular: 'List',
    description: 'Organize addresses into lists for tracking and automation.',
    emptyTitle: 'No lists yet',
    emptyMessage:
      'Add this address to a list to organize and track it alongside similar addresses.',
    color: 'blue',
    basePath: '/lists',
    createPath: '/lists/new',
  },
  alert: {
    icon: <BellIcon width={20} height={20} />,
    title: 'Alerts',
    singular: 'Alert',
    description: 'Get notified when specific conditions are met.',
    emptyTitle: 'No alerts configured',
    emptyMessage:
      'Create an alert to get notified when this address sends or receives transactions.',
    color: 'amber',
    basePath: '/alerts',
    createPath: '/alerts/new',
  },
  workflow: {
    icon: <GearIcon width={20} height={20} />,
    title: 'Workflows',
    singular: 'Workflow',
    description: 'Automate actions based on on-chain activity.',
    emptyTitle: 'No workflows',
    emptyMessage:
      'Create a workflow to automatically trigger actions when this address is involved in transactions.',
    color: 'violet',
    basePath: '/workflows',
    createPath: '/workflows/create',
  },
};

// Placeholder items for demo
const getPlaceholderItems = (type: ChipType): InstrumentationItem[] => {
  if (type === 'list') {
    return [
      {
        id: '0',
        name: 'OFAC Sanctions',
        description: 'US Treasury sanctioned addresses',
        status: 'active',
        createdAt: '2024-01-01',
        readonly: true,
      },
      {
        id: '1',
        name: 'Whale Wallets',
        description: 'Addresses holding >1000 ETH',
        status: 'active',
        createdAt: '2024-01-15',
      },
      {
        id: '2',
        name: 'DeFi Power Users',
        description: 'Frequent Uniswap & Aave users',
        status: 'active',
        createdAt: '2024-02-20',
      },
      {
        id: '3',
        name: 'MEV Bots',
        description: 'Known MEV bot addresses',
        status: 'active',
        createdAt: '2024-03-10',
      },
    ];
  }
  if (type === 'alert') {
    return [
      {
        id: '1',
        name: 'Large Transfer',
        description: 'Notify when transfer > 10 ETH',
        status: 'active',
        createdAt: '2024-01-20',
      },
      {
        id: '2',
        name: 'Token Swap',
        description: 'Alert on any DEX swap',
        status: 'paused',
        createdAt: '2024-02-15',
      },
      {
        id: '3',
        name: 'Contract Deploy',
        description: 'Notify on contract creation',
        status: 'active',
        createdAt: '2024-03-01',
      },
      {
        id: '4',
        name: 'NFT Transfer',
        description: 'Track NFT movements',
        status: 'draft',
        createdAt: '2024-03-15',
      },
    ];
  }
  if (type === 'workflow') {
    return [
      {
        id: '1',
        name: 'Whale Alert Discord',
        description: 'Post large txs to #whale-alerts',
        status: 'active',
        createdAt: '2024-02-01',
      },
      {
        id: '2',
        name: 'Daily Summary Email',
        description: 'Email daily activity digest',
        status: 'active',
        createdAt: '2024-02-10',
      },
    ];
  }
  return [];
};

const StatusBadge = ({ status }: { status?: string }) => {
  if (!status) return null;

  const config = {
    active: { color: 'green' as const, label: 'Active' },
    paused: { color: 'amber' as const, label: 'Paused' },
    draft: { color: 'gray' as const, label: 'Draft' },
  };

  const { color, label } =
    config[status as keyof typeof config] || config.draft;

  return (
    <Badge color={color} variant="soft" size="1">
      {label}
    </Badge>
  );
};

export default function InstrumentationPanel({
  open,
  onOpenChange,
  type,
  entityAddress,
  entityName,
  items,
  onCreateNew,
  onItemClick,
  onItemEdit,
  onItemDelete,
  onItemToggle,
}: InstrumentationPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const { effectiveTheme } = useTheme();
  const navigate = useNavigate();
  const config = typeConfig[type];

  // Use placeholder items if none provided
  const allItems = items ?? getPlaceholderItems(type);

  // Filter items by search
  const filteredItems = allItems.filter(
    (item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCreateNew = () => {
    if (onCreateNew) {
      onCreateNew();
    } else {
      // Default behavior: navigate to the create page
      onOpenChange(false);
      navigate(config.createPath);
    }
  };

  const handleItemClick = (item: InstrumentationItem) => {
    onItemClick?.(item);
    onOpenChange(false);
    navigate(`${config.basePath}/${item.id}`);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Theme appearance={effectiveTheme} asChild>
          <div>
            <Dialog.Overlay className="instrumentation-panel-overlay" />
            <Dialog.Content
              className={`instrumentation-panel-content ${effectiveTheme}`}
            >
              {/* Header */}
              <div className="instrumentation-panel-header">
                <Flex align="start" justify="between" mb="2">
                  <Flex align="center" gap="2">
                    <Box
                      style={{
                        padding: '8px',
                        borderRadius: '8px',
                        backgroundColor: `var(--${config.color}-3)`,
                        color: `var(--${config.color}-11)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {config.icon}
                    </Box>
                    <Box>
                      <Flex align="baseline" gap="2">
                        <Dialog.Title asChild>
                          <Text size="5" weight="bold">
                            {config.title}
                          </Text>
                        </Dialog.Title>
                        <Text size="1" color="gray">
                          {allItems.length}{' '}
                          {allItems.length === 1
                            ? config.singular.toLowerCase()
                            : config.title.toLowerCase()}
                        </Text>
                      </Flex>
                      <Dialog.Description asChild>
                        <Text size="2" color="gray">
                          {config.description}
                        </Text>
                      </Dialog.Description>
                    </Box>
                  </Flex>
                  <Dialog.Close asChild>
                    <IconButton variant="ghost" color="gray" size="2">
                      <Cross2Icon width={18} height={18} />
                    </IconButton>
                  </Dialog.Close>
                </Flex>

                {/* Entity info */}
                <Card size="1" mb="3">
                  <Flex align="center" gap="2">
                    <Text size="1" color="gray">
                      Address:
                    </Text>
                    <Text
                      size="1"
                      style={{
                        fontFamily: 'monospace',
                        wordBreak: 'break-all',
                      }}
                    >
                      {entityAddress}
                    </Text>
                  </Flex>
                  {entityName && (
                    <Text size="2" weight="medium" mt="1">
                      {entityName}
                    </Text>
                  )}
                </Card>

                {/* Search and Create */}
                <Flex gap="2" mb="3">
                  <Box style={{ flex: 1 }}>
                    <TextField.Root
                      placeholder={`Search ${config.title.toLowerCase()}...`}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    >
                      <TextField.Slot>
                        <MagnifyingGlassIcon width={16} height={16} />
                      </TextField.Slot>
                    </TextField.Root>
                  </Box>
                  <Button onClick={handleCreateNew}>
                    <PlusIcon />
                    New
                  </Button>
                </Flex>
              </div>

              {/* Content */}
              <ScrollArea className="instrumentation-panel-scroll">
                {filteredItems.length > 0 ? (
                  <Table.Root size="1">
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell width="70px">
                          Status
                        </Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell width="60px" />
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {filteredItems.map((item) => (
                        <Table.Row
                          key={item.id}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleItemClick(item)}
                        >
                          <Table.Cell>
                            <Flex direction="column">
                              <Flex align="center" gap="1">
                                <Text size="2" weight="medium">
                                  {item.name}
                                </Text>
                                {item.readonly && (
                                  <Tooltip content="System-managed list (read-only)">
                                    <Box
                                      style={{
                                        color: 'var(--gray-9)',
                                        display: 'flex',
                                      }}
                                    >
                                      <LockClosedIcon width={12} height={12} />
                                    </Box>
                                  </Tooltip>
                                )}
                              </Flex>
                              {item.description && (
                                <Text
                                  size="1"
                                  color="gray"
                                  style={{ display: 'block' }}
                                >
                                  {item.description}
                                </Text>
                              )}
                            </Flex>
                          </Table.Cell>
                          <Table.Cell>
                            <StatusBadge status={item.status} />
                          </Table.Cell>
                          <Table.Cell>
                            {item.readonly ? null : (
                              <Flex gap="1" justify="end" align="center">
                                {type !== 'list' && item.status !== 'draft' && (
                                  <Tooltip
                                    content={
                                      item.status === 'active'
                                        ? 'Pause'
                                        : 'Resume'
                                    }
                                  >
                                    <IconButton
                                      size="1"
                                      variant="ghost"
                                      color="gray"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        onItemToggle?.(item);
                                      }}
                                    >
                                      {item.status === 'active' ? (
                                        <PauseIcon width={14} height={14} />
                                      ) : (
                                        <PlayIcon width={14} height={14} />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip content="Edit">
                                  <IconButton
                                    size="1"
                                    variant="ghost"
                                    color="gray"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onItemEdit?.(item);
                                    }}
                                  >
                                    <Pencil1Icon width={14} height={14} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip content="Remove">
                                  <IconButton
                                    size="1"
                                    variant="ghost"
                                    color="red"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onItemDelete?.(item);
                                    }}
                                  >
                                    <TrashIcon width={14} height={14} />
                                  </IconButton>
                                </Tooltip>
                              </Flex>
                            )}
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : searchQuery ? (
                  <Card size="2">
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      gap="3"
                      py="6"
                    >
                      <MagnifyingGlassIcon
                        width={32}
                        height={32}
                        color="var(--gray-8)"
                      />
                      <Text size="2" color="gray" align="center">
                        No {config.title.toLowerCase()} match &ldquo;
                        {searchQuery}
                        &rdquo;
                      </Text>
                      <Button
                        size="2"
                        variant="soft"
                        onClick={() => setSearchQuery('')}
                      >
                        Clear search
                      </Button>
                    </Flex>
                  </Card>
                ) : (
                  <Card size="2">
                    <Flex
                      direction="column"
                      align="center"
                      justify="center"
                      gap="3"
                      py="6"
                    >
                      <Box
                        style={{
                          padding: '16px',
                          borderRadius: '50%',
                          backgroundColor: `var(--${config.color}-3)`,
                          color: `var(--${config.color}-9)`,
                        }}
                      >
                        {config.icon}
                      </Box>
                      <Box style={{ textAlign: 'center' }}>
                        <Text size="3" weight="medium" mb="1">
                          {config.emptyTitle}
                        </Text>
                        <Text
                          size="2"
                          color="gray"
                          style={{ maxWidth: 280, display: 'block' }}
                        >
                          {config.emptyMessage}
                        </Text>
                      </Box>
                      <Button size="2" onClick={handleCreateNew}>
                        <PlusIcon />
                        Create {config.singular}
                      </Button>
                    </Flex>
                  </Card>
                )}
              </ScrollArea>
            </Dialog.Content>
          </div>
        </Theme>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
